/**
 * Audience-aware loader for the NSUK support widgets.
 *
 * The student and staff widgets are exposed by the same browser bundle. This
 * module owns that bundle's lifecycle so portal roots do not load assets or
 * initialise widgets more than once.
 */

const SCRIPT_ID = "student-support-widget-script";
const STYLE_ID = "student-support-widget-style";
const DISMISS_BUTTON_ID = "support-widget-dismiss-button";
const DISMISS_STYLE_ID = "support-widget-dismiss-style";
const TEST_OVERRIDES_STYLE_ID = "student-support-widget-test-overrides";
const DEFAULT_ASSET_URL = "https://support-widget.nsuk.edu.ng";
const DEFAULT_BACKEND_URL = "https://support-api.nsuk.edu.ng";
const TEST_ASSET_URL = "https://widget-production-f253.up.railway.app";
const TEST_BACKEND_URL = "https://backend-production-feeb0.up.railway.app";
const SUPPORT_DOMAIN_ID = "nasarawa-state-university";

let scriptPromise = null;
let activeWidget = null;
let activeKey = null;
let pendingKey = null;
let pendingPromise = null;
let lifecycleVersion = 0;
let dismissed = false;
let testLayoutObserver = null;

function trimTrailingSlashes(value) {
  return value.replace(/\/+$/, "");
}

export function isTestSubdomain(hostname) {
  const host = (hostname === undefined
    ? window.location.hostname
    : hostname
  ).toLowerCase().replace(/\.$/, "");

  return host.startsWith("test.");
}

function usesTestStudentWidget(audience) {
  return audience !== "staff" && isTestSubdomain();
}

function assetBaseUrl(audience) {
  if (usesTestStudentWidget(audience)) return TEST_ASSET_URL;

  return trimTrailingSlashes(
    process.env.VUE_APP_SUPPORT_WIDGET_URL || DEFAULT_ASSET_URL
  );
}

function backendUrl(audience) {
  if (usesTestStudentWidget(audience)) return TEST_BACKEND_URL;

  return trimTrailingSlashes(
    process.env.VUE_APP_SUPPORT_API_URL || DEFAULT_BACKEND_URL
  );
}

/**
 * Resolve the support domain configured for a portal origin.
 *
 * @param {string} [hostname]
 * @returns {"nasarawa-state-university"|null}
 */
export function deriveDomainId(hostname) {
  const host = (hostname === undefined
    ? window.location.hostname
    : hostname
  ).toLowerCase().replace(/\.$/, "");

  if (host === "localhost" || host === "127.0.0.1" || host === "::1") {
    return SUPPORT_DOMAIN_ID;
  }

  if (isTestSubdomain(host)) {
    return SUPPORT_DOMAIN_ID;
  }

  if (host === "nsuk.edu.ng" || host === "www.nsuk.edu.ng") {
    return SUPPORT_DOMAIN_ID;
  }

  return /^(test|pg|ug)\.nsuk\.edu\.ng$/.test(host)
    ? SUPPORT_DOMAIN_ID
    : null;
}

export function isEligibleHost(hostname) {
  return deriveDomainId(hostname) !== null;
}

function ensureStyle(audience) {
  if (document.getElementById(STYLE_ID)) return;

  const link = document.createElement("link");
  link.id = STYLE_ID;
  link.rel = "stylesheet";
  link.href = `${assetBaseUrl(audience)}/style.css`;
  document.head.appendChild(link);
}

function ensureTestOverrides() {
  if (document.getElementById(TEST_OVERRIDES_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = TEST_OVERRIDES_STYLE_ID;
  style.textContent = ".ssw-suggestions { display: none !important; }";
  document.head.appendChild(style);
}

function ensureTestExpandedLayout() {
  if (testLayoutObserver) return;

  const expandWhenAvailable = () => {
    const button = document.querySelector(
      ".ssw-expand-btn[aria-label='Show tickets']"
    );
    if (!button) return false;

    button.click();
    testLayoutObserver?.disconnect();
    testLayoutObserver = null;
    return true;
  };

  if (expandWhenAvailable()) return;

  testLayoutObserver = new MutationObserver(expandWhenAvailable);
  testLayoutObserver.observe(document.body, { childList: true, subtree: true });
}

function loadAssets(audience) {
  ensureStyle(audience);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const handleLoad = () => resolve();
    const handleError = () => reject(new Error("Failed to load the support widget bundle"));
    const existing = document.getElementById(SCRIPT_ID);

    if (existing) {
      if (widgetForAudience(audience)) {
        resolve();
        return;
      }
      existing.addEventListener("load", handleLoad, { once: true });
      existing.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `${assetBaseUrl(audience)}/student-support-widget.iife.js`;
    script.async = true;
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    document.body.appendChild(script);
  });

  return scriptPromise;
}

function widgetForAudience(audience) {
  return audience === "staff"
    ? window.StaffSupportWidget
    : window.StudentSupportWidget;
}

function ensureDismissStyle() {
  if (document.getElementById(DISMISS_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = DISMISS_STYLE_ID;
  style.textContent = `
    #${DISMISS_BUTTON_ID} {
      position: fixed;
      right: max(12px, env(safe-area-inset-right));
      bottom: calc(76px + env(safe-area-inset-bottom));
      z-index: 2147483647;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border: 2px solid #fff;
      border-radius: 50%;
      background: #374151;
      color: #fff;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.24);
      cursor: pointer;
      transition: background-color 150ms ease, transform 150ms ease;
    }

    #${DISMISS_BUTTON_ID}:hover {
      background: #111827;
      transform: scale(1.06);
    }

    #${DISMISS_BUTTON_ID}:focus-visible {
      outline: 3px solid rgba(21, 101, 192, 0.4);
      outline-offset: 2px;
    }

    #${DISMISS_BUTTON_ID} svg {
      width: 16px;
      height: 16px;
      pointer-events: none;
    }

    @media (prefers-reduced-motion: reduce) {
      #${DISMISS_BUTTON_ID} {
        transition: none;
      }
    }
  `;
  document.head.appendChild(style);
}

function removeDismissButton() {
  const button = document.getElementById(DISMISS_BUTTON_ID);
  if (button) button.remove();
}

function dismiss() {
  dismissed = true;
  destroy();
}

function showDismissButton() {
  if (dismissed || document.getElementById(DISMISS_BUTTON_ID)) return;

  ensureDismissStyle();

  const button = document.createElement("button");
  button.id = DISMISS_BUTTON_ID;
  button.type = "button";
  button.title = "Hide support";
  button.setAttribute("aria-label", "Hide support widget");
  button.innerHTML = `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
    </svg>
  `;
  button.addEventListener("click", dismiss);
  document.body.appendChild(button);
}

function configKey(audience, config) {
  return [
    audience,
    config.domainId,
    config.backendUrl,
    config.studentId || "",
    config.apiKey || "",
  ].join("|");
}

function destroy() {
  lifecycleVersion += 1;

  testLayoutObserver?.disconnect();
  testLayoutObserver = null;

  if (activeWidget && typeof activeWidget.destroy === "function") {
    activeWidget.destroy();
  }

  activeWidget = null;
  activeKey = null;
  pendingKey = null;
  pendingPromise = null;
  removeDismissButton();
}

async function initialize(audience, audienceConfig = {}) {
  if (dismissed) return false;

  // The test rollout is exclusively for the out-of-the-box student widget.
  if (audience === "staff" && isTestSubdomain()) return false;

  const domainId = deriveDomainId();
  if (!domainId) return false;

  const testStudentWidget = usesTestStudentWidget(audience);
  const config = testStudentWidget
    ? {
        studentId: audienceConfig.studentId,
        studentName: audienceConfig.studentName,
        studentEmail: audienceConfig.studentEmail,
        backendUrl: backendUrl(audience),
      }
    : {
        ...audienceConfig,
        domainId,
        backendUrl: backendUrl(audience),
      };
  Object.keys(config).forEach((key) => {
    if (config[key] === undefined) delete config[key];
  });
  const key = configKey(audience, config);

  if (activeKey === key) return true;
  if (pendingKey === key && pendingPromise) return pendingPromise;

  if (activeWidget || pendingPromise) destroy();

  const version = lifecycleVersion;
  let request;
  request = (async () => {
    try {
      await loadAssets(audience);
      if (version !== lifecycleVersion) return false;

      const widget = widgetForAudience(audience);
      if (!widget || typeof widget.init !== "function") {
        throw new Error(`The ${audience} support widget is unavailable`);
      }

      widget.init(config);
      if (version !== lifecycleVersion) {
        if (typeof widget.destroy === "function") widget.destroy();
        return false;
      }

      activeWidget = widget;
      activeKey = key;
      if (testStudentWidget) {
        ensureTestOverrides();
        ensureTestExpandedLayout();
      } else {
        showDismissButton();
      }
      return true;
    } catch (error) {
      // Support is best-effort and must never prevent a portal from loading.
      // eslint-disable-next-line no-console
      console.warn(`[SupportWidget] failed to initialise ${audience} support`, error);
      return false;
    } finally {
      if (pendingPromise === request) {
        pendingKey = null;
        pendingPromise = null;
      }
    }
  })();

  pendingKey = key;
  pendingPromise = request;
  return request;
}

const SupportWidget = {
  initGuest() {
    return initialize("guest");
  },

  initStudent({
    studentId,
    studentName,
    studentEmail,
    apiKey,
  } = {}) {
    if (!studentId || (!apiKey && !usesTestStudentWidget("student"))) {
      // Never let a partial authenticated bootstrap silently become guest mode.
      // eslint-disable-next-line no-console
      console.warn("[SupportWidget] authenticated student bootstrap is incomplete");
      return Promise.resolve(false);
    }

    return initialize("student", {
      studentId,
      studentName,
      studentEmail,
      apiKey,
    });
  },

  initStaff() {
    return initialize("staff");
  },

  destroy,
  isEligibleHost,
  requiresStudentBootstrap() {
    return !usesTestStudentWidget("student");
  },
};

export default SupportWidget;
